import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Mail, ArrowLeft, Loader2, CheckCircle2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VerifyEmailPending = () => {
  const location = useLocation();
  const email = location.state?.email || "";
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  const handleResendEmail = async () => {
    if (!email) {
      toast.error("Email non disponible. Veuillez vous réinscrire.");
      return;
    }

    setResending(true);
    try {
      await axios.post(`${API}/auth/resend-verification`, { email });
      setResent(true);
      toast.success("Un nouveau lien de vérification a été envoyé !");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-violet-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Back Link */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-8 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Retour à l'accueil</span>
        </Link>

        <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 p-8 border border-slate-100 text-center">
          {/* Icon */}
          <div className="w-20 h-20 bg-gradient-to-br from-violet-100 to-pink-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Mail className="w-10 h-10 text-violet-600" />
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900 mb-3">
            Vérifiez votre email
          </h1>

          {/* Description */}
          <p className="text-slate-500 mb-2">
            Nous avons envoyé un lien de vérification à :
          </p>
          
          {email && (
            <p className="font-medium text-slate-800 mb-6 bg-slate-50 py-2 px-4 rounded-lg inline-block">
              {email}
            </p>
          )}

          <p className="text-slate-500 mb-8">
            Cliquez sur le lien dans l'email pour activer votre compte et accéder à ResponsIA.
          </p>

          {/* Tips */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 text-left">
            <p className="text-amber-800 text-sm font-medium mb-2">
              Vous ne trouvez pas l'email ?
            </p>
            <ul className="text-amber-700 text-sm space-y-1">
              <li>• Vérifiez votre dossier spam/courrier indésirable</li>
              <li>• L'email peut prendre quelques minutes pour arriver</li>
              <li>• Vérifiez que l'adresse email est correcte</li>
            </ul>
          </div>

          {/* Resend Button */}
          {resent ? (
            <div className="flex items-center justify-center gap-2 text-emerald-600 mb-4">
              <CheckCircle2 className="w-5 h-5" />
              <span>Email renvoyé avec succès !</span>
            </div>
          ) : (
            <Button
              onClick={handleResendEmail}
              disabled={resending || !email}
              variant="outline"
              className="w-full h-12 rounded-full border-2 border-violet-200 text-violet-600 hover:bg-violet-50 mb-4"
              data-testid="resend-verification-button"
            >
              {resending ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Envoi en cours...
                </>
              ) : (
                "Renvoyer l'email de vérification"
              )}
            </Button>
          )}

          {/* Login Link */}
          <p className="text-slate-500">
            Déjà vérifié ?{" "}
            <Link to="/login" className="text-violet-600 hover:text-violet-700 font-medium">
              Se connecter
            </Link>
          </p>
        </div>

        {/* Footer */}
        <p className="mt-6 text-center text-sm text-slate-400">
          Le lien de vérification expire dans 24 heures.
        </p>
      </div>
    </div>
  );
};

export default VerifyEmailPending;
